/**
 * Filter controls panel for transit search.
 * Includes hours, depth, vmag, altitude, moon filter, and search button.
 */
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Slider,
  Box,
  FormControlLabel,
  Switch,
  Checkbox,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Science as ScienceIcon,
  NightsStay as MoonIcon,
} from '@mui/icons-material';
import { astronomyService, LUNAR_CYCLE_DAYS, FULL_MOON_DAYS } from '../../../services/astronomyService';
import { renderProjectOption, projectValueLabel } from './projectOptionLabel';

const TransitFilterPanel = ({
  filters,
  setFilters,
  showOnlyFullTransits,
  setShowOnlyFullTransits,
  selectedObservatoryId,
  observatories,
  handleObservatoryChange,
  projects,
  selectedProjectId,
  setSelectedProjectId,
  loading,
  onSearch,
}) => {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Search Filters
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Observatory</InputLabel>
              <Select
                value={selectedObservatoryId || ''}
                onChange={(e) => handleObservatoryChange(e.target.value)}
                label="Observatory"
              >
                {observatories.map((obs) => (
                  <MenuItem key={obs._id} value={obs._id}>
                    {obs.name} ({obs.location?.city || 'Unknown location'})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <Select
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                label="Project"
                renderValue={(val) => projectValueLabel(projects.find((p) => p._id === val))}
              >
                {projects
                  .slice()
                  .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }))
                  .map((project) => (
                    <MenuItem key={project._id} value={project._id}>
                      {renderProjectOption(project)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Time Window (hours)"
              value={filters.hours}
              onChange={(e) => setFilters({ ...filters, hours: parseFloat(e.target.value) })}
              inputProps={{ min: 1, max: 168, step: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DepthSlider filters={filters} setFilters={setFilters} />
          </Grid>
          <Grid item xs={12} md={3}>
            <VMagSlider filters={filters} setFilters={setFilters} />
          </Grid>
          <Grid item xs={12} md={3}>
            <AltitudeSlider filters={filters} setFilters={setFilters} />
          </Grid>
        </Grid>

        <MoonFilterSection filters={filters} setFilters={setFilters} />

        {selectedObservatoryId && (
          <Box mt={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyFullTransits}
                  onChange={(e) => setShowOnlyFullTransits(e.target.checked)}
                  color="primary"
                />
              }
              label="Show only full transits (both ingress and egress observable)"
            />
          </Box>
        )}
        <Box mt={2} display="flex" justifyContent="center">
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={onSearch}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <ScienceIcon />}
          >
            {loading ? 'Searching...' : 'Search for Transits'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

const DepthSlider = ({ filters, setFilters }) => (
  <Box>
    <Typography gutterBottom>
      Min Transit Depth: {filters.minDepth}%
    </Typography>
    <Slider
      value={filters.minDepth}
      onChange={(e, value) => setFilters({ ...filters, minDepth: value })}
      min={0.1}
      max={5}
      step={0.1}
      marks={[
        { value: 0.5, label: '0.5%' },
        { value: 2, label: '2%' },
        { value: 5, label: '5%' },
      ]}
    />
  </Box>
);

const VMagSlider = ({ filters, setFilters }) => (
  <Box>
    <Typography gutterBottom>
      Max V Magnitude: {filters.maxVMag}
    </Typography>
    <Slider
      value={filters.maxVMag}
      onChange={(e, value) => setFilters({ ...filters, maxVMag: value })}
      min={8}
      max={17}
      step={0.5}
      marks={[
        { value: 10, label: '10' },
        { value: 14, label: '14' },
        { value: 17, label: '17' },
      ]}
    />
  </Box>
);

const AltitudeSlider = ({ filters, setFilters }) => (
  <Box>
    <Typography gutterBottom>
      Min Altitude: {filters.minAltitude}
    </Typography>
    <Slider
      value={filters.minAltitude}
      onChange={(e, value) => setFilters({ ...filters, minAltitude: value })}
      min={10}
      max={60}
      step={5}
      marks={[
        { value: 20, label: '20' },
        { value: 30, label: '30' },
        { value: 45, label: '45' },
      ]}
    />
  </Box>
);

const MoonFilterSection = ({ filters, setFilters }) => (
  <Box mt={2}>
    <FormControlLabel
      control={
        <Switch
          checked={filters.enableMoonFilter}
          onChange={(e) => setFilters({ ...filters, enableMoonFilter: e.target.checked })}
          color="primary"
        />
      }
      label={
        <Box display="flex" alignItems="center" gap={0.5}>
          <MoonIcon fontSize="small" />
          <Typography>Moon Separation Filter</Typography>
        </Box>
      }
    />
    {filters.enableMoonFilter && (
      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item xs={12} md={4}>
          <Box>
            <Typography gutterBottom>
              Peak Separation (Full Moon): {filters.moonPeakSeparation}
            </Typography>
            <Slider
              value={filters.moonPeakSeparation}
              onChange={(e, value) => setFilters({ ...filters, moonPeakSeparation: value })}
              min={10}
              max={120}
              step={5}
              marks={[
                { value: 30, label: '30' },
                { value: 60, label: '60' },
                { value: 90, label: '90' },
                { value: 120, label: '120' },
              ]}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box>
            <Typography gutterBottom>
              Moon Sensitivity: {filters.moonLorentzianWidth <= 3 ? 'Low' : filters.moonLorentzianWidth <= 7 ? 'Medium' : 'High'}
            </Typography>
            <Slider
              value={filters.moonLorentzianWidth}
              onChange={(e, value) => setFilters({ ...filters, moonLorentzianWidth: value })}
              min={1}
              max={10}
              step={0.5}
              marks={[
                { value: 1, label: 'Low' },
                { value: 5, label: 'Med' },
                { value: 10, label: 'High' },
              ]}
            />
            <Typography variant="caption" color="text.secondary">
              Low = only near full moon, High = most of lunar cycle
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom component="div">
              Required separation by lunar phase:
            </Typography>
            <Typography variant="caption" component="div">
              New Moon (day 0): {Math.round(astronomyService.calculateLorentzianMoonSeparation(0, filters.moonPeakSeparation, filters.moonLorentzianWidth))}°
            </Typography>
            <Typography variant="caption" component="div">
              Quarter (day {(LUNAR_CYCLE_DAYS / 4).toFixed(1)}): {Math.round(astronomyService.calculateLorentzianMoonSeparation(LUNAR_CYCLE_DAYS / 4, filters.moonPeakSeparation, filters.moonLorentzianWidth))}°
            </Typography>
            {/* #1157: full moon is at HALF a lunar cycle, 14.765 days -- not the 14 this row
                used to claim, which reported a requirement 1.4% below the peak as if it were
                the peak. */}
            <Typography variant="caption" component="div">
              Full Moon (day {FULL_MOON_DAYS.toFixed(1)}): {Math.round(astronomyService.calculateLorentzianMoonSeparation(FULL_MOON_DAYS, filters.moonPeakSeparation, filters.moonLorentzianWidth))}°
            </Typography>
          </Box>
        </Grid>
      </Grid>
    )}
  </Box>
);

export default TransitFilterPanel;
